import type { OrderListDto, OrderResponseDto } from "@/types/order.dto";
import type { SettlementResponseDto } from "@/types/wallet.dto";
import type {
  DeliveryDashboardStatsDto,
  DeliveryPartnerResponseDto,
} from "@/types/delivery.dto";
import type { DeliveryEarningRow, DeliveryOrderRow, DeliveryProfileUi } from "@/types/delivery.ui";
import { DeliveryPartnerStatus, OrderStatus, PaymentStatus, SettlementStatus } from "@/types/enums";
import { formatOrderDate, mapPaymentMethodToUi } from "./order-mappers";

export function mapOrderStatusToDeliveryUi(status: OrderStatus): DeliveryOrderRow["status"] {
  switch (status) {
    case OrderStatus.ReadyForPickup:
      return "assigned";
    case OrderStatus.OutForDelivery:
      return "in_transit";
    case OrderStatus.Delivered:
      return "delivered";
    default:
      return "assigned";
  }
}

function formatAddress(address?: OrderResponseDto["shippingAddress"]): string {
  if (!address) return "—";
  return [address.addressLine1, address.addressLine2, address.landmark, address.city, address.pincode]
    .filter(Boolean)
    .join(", ");
}

function formatEstimatedTime(minutes?: number): string {
  if (!minutes) return "—";
  return `${minutes} min`;
}

export function mapOrderToDeliveryRow(
  list: OrderListDto,
  detail?: OrderResponseDto | null,
  storeAddress?: string,
): DeliveryOrderRow {
  const paymentMethod = mapPaymentMethodToUi(detail?.paymentMethod ?? list.paymentMethod);

  return {
    id: String(list.id),
    orderId: list.orderNumber,
    customerName: detail?.customerName ?? list.customerName,
    customerPhone: detail?.customerPhone ?? "—",
    customerAddress: formatAddress(detail?.shippingAddress),
    storeName: detail?.storeName ?? list.storeName,
    storeAddress: storeAddress ?? detail?.storeName ?? list.storeName,
    items: list.itemCount,
    total: list.totalAmount,
    commission: detail?.deliveryCharge ?? 0,
    status: mapOrderStatusToDeliveryUi(list.status),
    distance: "—",
    estimatedTime: formatEstimatedTime(detail?.estimatedDeliveryMinutes),
    orderDate: formatOrderDate(list.createdAt),
    paymentMethod,
    codAmount: paymentMethod === "cod" ? list.totalAmount : undefined,
  };
}

export function mapOrderListDtosToDeliveryRows(
  items: OrderListDto[],
  details: Map<number, OrderResponseDto>,
  storeAddresses: Map<number, string>,
): DeliveryOrderRow[] {
  return items.map((item) =>
    mapOrderToDeliveryRow(item, details.get(item.id), storeAddresses.get(details.get(item.id)?.storeId ?? 0)),
  );
}

export function mapSettlementToDeliveryEarning(dto: SettlementResponseDto): DeliveryEarningRow {
  const statusMap: Record<SettlementStatus, DeliveryEarningRow["status"]> = {
    [SettlementStatus.Pending]: "pending",
    [SettlementStatus.Processing]: "processing",
    [SettlementStatus.Completed]: "paid",
    [SettlementStatus.Failed]: "pending",
  };

  return {
    id: String(dto.id),
    period: `${formatOrderDate(dto.periodStart)} – ${formatOrderDate(dto.periodEnd)}`,
    deliveries: dto.orderCount,
    totalEarnings: dto.amount,
    tips: 0,
    bonuses: 0,
    status: statusMap[dto.status] ?? "pending",
    paidDate: dto.processedAt ? formatOrderDate(dto.processedAt) : undefined,
  };
}

export function mapSettlementsToDeliveryEarnings(items: SettlementResponseDto[]): DeliveryEarningRow[] {
  return items.map(mapSettlementToDeliveryEarning);
}

export function mapPartnerToProfileUi(dto: DeliveryPartnerResponseDto): DeliveryProfileUi {
  const statusMap: Record<number, DeliveryProfileUi["status"]> = {
    [DeliveryPartnerStatus.Pending]: "pending",
    [DeliveryPartnerStatus.Active]: "active",
    [DeliveryPartnerStatus.Suspended]: "suspended",
    [DeliveryPartnerStatus.Rejected]: "rejected",
  };

  return {
    id: dto.id,
    name: dto.fullName,
    phone: dto.phone,
    email: dto.email ?? "",
    vehicleType: dto.vehicleType,
    vehicleNumber: dto.vehicleNumber ?? "",
    licenseNumber: "",
    rating: dto.rating,
    totalDeliveries: dto.totalDeliveries,
    joinedDate: formatOrderDate(dto.createdAt),
    isOnline: dto.isOnline,
    isAvailable: dto.isAvailable,
    city: dto.city,
    commissionPerDelivery: dto.commissionPerDelivery,
    totalEarnings: dto.totalEarnings,
    status: statusMap[dto.status] ?? "pending",
  };
}

export function profileUiToUpdateDto(profile: DeliveryProfileUi) {
  return {
    fullName: profile.name,
    phone: profile.phone,
    email: profile.email || undefined,
    vehicleType: profile.vehicleType,
    vehicleNumber: profile.vehicleNumber || undefined,
    city: profile.city,
    serviceRadius: 10,
  };
}

export function getDeliveryOrderAdvanceAction(status: DeliveryOrderRow["status"]): string | null {
  const labels: Record<string, string> = {
    assigned: "Pick Up",
    picked_up: "Start Delivery",
    in_transit: "Mark Delivered",
  };
  return labels[status] ?? null;
}

export function computeCompletionRate(stats: DeliveryDashboardStatsDto): number {
  if (stats.totalDeliveries === 0) return 100;
  const completed = stats.totalDeliveries - stats.pendingDeliveries;
  return Math.round((completed / stats.totalDeliveries) * 1000) / 10;
}

export function getPartnerInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
